import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { lettersApi } from '../../services/api';

interface RoleRow {
  id: number;
  name: string;
  email: string;
  is_admin: boolean | number;
  is_authorized_signatory: boolean | number;
}

export function LettersRoleAssignmentPage() {
  const [rows, setRows] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await lettersApi.listRoleAssignments();
      setRows(response.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (row: RoleRow, field: 'is_admin' | 'is_authorized_signatory') => {
    try {
      setSavingId(row.id);
      setError('');
      const nextValue = !(row[field] === true || row[field] === 1);
      const payload = {
        is_admin: field === 'is_admin' ? nextValue : (row.is_admin === true || row.is_admin === 1),
        is_authorized_signatory: field === 'is_authorized_signatory' ? nextValue : (row.is_authorized_signatory === true || row.is_authorized_signatory === 1),
      };
      await lettersApi.updateRoleAssignment(row.id, payload);
      setRows((prev) => prev.map((item) => (item.id === row.id ? { ...item, ...payload } : item)));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update role.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Role Assignment</h1>
        <p className="text-gray-600 mt-2">Enable Admin Rights and Authorized Signatory per user.</p>
      </div>

      {error && <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">⚠ {error}</div>}

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-gray-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading users...</div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">User</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-center px-4 py-3 font-semibold">Admin Rights</th>
                  <th className="text-center px-4 py-3 font-semibold">Authorized Signatory</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const adminEnabled = row.is_admin === true || row.is_admin === 1;
                  const signatoryEnabled = row.is_authorized_signatory === true || row.is_authorized_signatory === 1;
                  const disabled = savingId === row.id;
                  return (
                    <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900 font-medium">{row.name}</td>
                      <td className="px-4 py-3 text-gray-600">{row.email}</td>
                      <td className="px-4 py-3 text-center">
                        <input type="checkbox" checked={adminEnabled} disabled={disabled} onChange={() => toggle(row, 'is_admin')} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input type="checkbox" checked={signatoryEnabled} disabled={disabled} onChange={() => toggle(row, 'is_authorized_signatory')} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
